import React from "react";
import {
  TextElement,
  StickerElement,
  CaptionElement,
  ToolType,
} from "../types";
import {
  ToolSelection,
  TextInputControls,
  CaptionInputControls,
  StickerSelectionControls,
  ElementsList,
  TextEditingControls,
  CaptionEditingControls,
  StickerEditingControls,
  SaveButton,
} from "./EditorControls";

interface MemeSidebarProps {
  tool: ToolType;
  onToolChange: (tool: ToolType) => void;
  newText: string;
  onTextChange: (text: string) => void;
  onAddText: () => void;
  newCaption: string;
  onCaptionChange: (text: string) => void;
  onAddCaption: () => void;
  onAddSticker: (stickerUrl: string, stickerName: string) => void;
  texts: TextElement[];
  stickers: StickerElement[];
  captions: CaptionElement[];
  selectedEntity: string | null;
  selectedText: TextElement | undefined;
  selectedCaption: CaptionElement | undefined;
  selectedSticker: StickerElement | undefined;
  saving: boolean;
  onEntitySelect: (id: string) => void;
  onDeleteEntity: (id: string) => void;
  onTextUpdate: (id: string, updates: Partial<TextElement>) => void;
  onCaptionUpdate: (id: string, updates: Partial<CaptionElement>) => void;
  onStickerUpdate: (id: string, updates: Partial<StickerElement>) => void;
  onSaveMeme: () => void;
  onDeselectEntity: () => void;
  isMobile?: boolean;
}

const MemeSidebar: React.FC<MemeSidebarProps> = ({
  tool,
  onToolChange,
  newText,
  onTextChange,
  onAddText,
  newCaption,
  onCaptionChange,
  onAddCaption,
  onAddSticker,
  texts,
  stickers,
  captions,
  selectedEntity,
  selectedText,
  selectedCaption,
  selectedSticker,
  saving,
  onEntitySelect,
  onDeleteEntity,
  onTextUpdate,
  onCaptionUpdate,
  onStickerUpdate,
  onSaveMeme,
  onDeselectEntity,
  isMobile = false,
}) => {
  const handleSidebarClick = (e: React.MouseEvent) => {
    // Check if click is outside the elements list container AND not on interactive controls
    const target = e.target as HTMLElement;
    const elementsListContainer = target.closest(".elements-list-container");
    const interactiveControl = target.closest(
      "button, input, select, textarea, [role='button']"
    );

    // Only deselect if clicking outside elements list AND not on any interactive control
    if (!elementsListContainer && !interactiveControl && selectedEntity) {
      onDeselectEntity();
    }
  };

  return (
    <div
      className={`flex flex-col gap-6 ${isMobile ? "gap-4" : "gap-8"}`}
      onClick={handleSidebarClick}
    >
      <div
        className={`bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-pink-100 ${
          isMobile ? "p-4" : "p-6"
        } ${isMobile ? "" : "sticky top-6"}`}
      >
        {tool === "none" && (
          <div className="text-center text-gray-500 py-2">
            <p className="text-xl">הוספת שטויות</p>
          </div>
        )}
        <ToolSelection tool={tool} onToolChange={onToolChange} />

        {tool === "text" && (
          <TextInputControls
            newText={newText}
            onTextChange={onTextChange}
            onAddText={onAddText}
          />
        )}

        {tool === "caption" && (
          <CaptionInputControls
            newCaption={newCaption}
            onCaptionChange={onCaptionChange}
            onAddCaption={onAddCaption}
          />
        )}

        {tool === "sticker" && (
          <StickerSelectionControls onAddSticker={onAddSticker} />
        )}
      </div>
      <div
        className={`bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-pink-100 ${
          isMobile ? "p-4" : "p-6"
        } ${isMobile ? "" : "sticky top-6"}`}
      >
        <div className="elements-list-container">
          <ElementsList
            texts={texts}
            stickers={stickers}
            captions={captions}
            selectedEntity={selectedEntity}
            onEntitySelect={onEntitySelect}
            onDeleteEntity={onDeleteEntity}
            onDeselectEntity={onDeselectEntity}
          />
        </div>

        {selectedText && (
          <TextEditingControls
            selectedText={selectedText}
            onTextUpdate={onTextUpdate}
          />
        )}

        {selectedCaption && (
          <CaptionEditingControls
            selectedCaption={selectedCaption}
            onCaptionUpdate={onCaptionUpdate}
          />
        )}

        {selectedSticker && (
          <StickerEditingControls
            selectedSticker={selectedSticker}
            onStickerUpdate={onStickerUpdate}
          />
        )}

        <SaveButton saving={saving} onSaveMeme={onSaveMeme} />
      </div>
    </div>
  );
};

export default MemeSidebar;
